'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox } from '@pickleballinc/ui/components/ui/checkbox';
import { useForm } from 'react-hook-form';

import { ActionButtons } from '@/components/molecules';
import { ApproveDeclineRequestData, approveDeclineRequestSchema } from '@/entities';

interface ApproveDeclineRequestProps {
	fullName: string;
	teamName: string;
	onSubmit: (data: ApproveDeclineRequestData) => Promise<void>;
	onClose: () => void;
	isPending?: boolean;
}

export const ApproveDeclineRequestForm = ({ fullName, onSubmit, onClose, teamName }: ApproveDeclineRequestProps) => {
	const form = useForm<ApproveDeclineRequestData>({
		resolver: zodResolver(approveDeclineRequestSchema),
		defaultValues: {
			approvePlayerRequest: false,
			declinePlayerRequest: false
		}
	});

	const { formState, handleSubmit, setValue, watch } = form;

	const isApproveChecked = watch('approvePlayerRequest');
	const isDeclineChecked = watch('declinePlayerRequest');

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<p className="py-4 text-lg font-semibold text-gray-500">
				{fullName} is requesting to join {teamName}
			</p>

			<div className="flex items-center">
				<Checkbox
					className="size-5 cursor-pointer rounded-md data-[state=checked]:bg-primary-100 data-[state=checked]:text-primary"
					name="approvePlayerRequest"
					checked={isApproveChecked}
					onCheckedChange={(checked: boolean) => {
						setValue('approvePlayerRequest', checked, { shouldDirty: true });
						setValue('declinePlayerRequest', false);
					}}
				/>
				<p className="pl-2">Approve - Add player to my team</p>
			</div>
			<div className="flex items-center pt-4">
				<Checkbox
					className="size-5 cursor-pointer rounded-md data-[state=checked]:bg-primary-100 data-[state=checked]:text-primary"
					name="declinePlayerRequest"
					checked={isDeclineChecked}
					onCheckedChange={(checked: boolean) => {
						setValue('declinePlayerRequest', checked, { shouldDirty: true });
						setValue('approvePlayerRequest', false);
					}}
				/>
				<p className="pl-2">Decline this request</p>
			</div>

			<ActionButtons
				className="flex place-self-end !px-[10px] !py-[16px]"
				closeButtonClassName="w-full"
				submitButtonClassName="w-full"
				onClose={onClose}
				submitButtonText="Save"
				closeButtonText="Cancel"
				submitButtonDisabled={!formState.isDirty || formState.isSubmitting}
			/>
		</form>
	);
};
